import { Injectable } from '@angular/core';
import { APP_CONFIG } from '@appConfig/_app-config_master';
import {
	APP_MODE,
	APP_MODE_SETTINGS
}                     from '@appConfig/app-mode-config';
import {
	INGXLoggerConfig,
	NGXLogger,
	NgxLoggerLevel
}                     from 'ngx-logger';

@Injectable( { providedIn: 'root' } )
export class LoggerService {

	private loggerConfigOptions: INGXLoggerConfig;

	private restoreConfig: boolean;

	constructor(
		private logger: NGXLogger
	) {
		this.setDefaultConfig();
	}

	private setLoggerConfig( configData ): INGXLoggerConfig {
		const loggerConfig: INGXLoggerConfig = {
			disableConsoleLogging: false,
			enableSourceMaps: false,
			httpResponseType: null,
			level: null,
			serverLogLevel: null,
			serverLoggingUrl: null
		};

		Object.keys( configData ).forEach( key => {
			if ( loggerConfig.hasOwnProperty( key ) ) { loggerConfig[ key ] = configData[ key ]; }
		} );
		return loggerConfig;
	}

	private setDefaultConfig() {
		switch ( APP_CONFIG.appMode ) {

			case APP_MODE.DEVELOP:
				this.loggerConfigOptions = this.setLoggerConfig( APP_MODE_SETTINGS.Develop.loggingConfigOptions );
				break;

			case APP_MODE.LOCAL:
				this.loggerConfigOptions = this.setLoggerConfig( APP_MODE_SETTINGS.Local.loggingConfigOptions );
				break;

			case APP_MODE.PRODUCTION:
				this.loggerConfigOptions = this.setLoggerConfig( APP_MODE_SETTINGS.Production.loggingConfigOptions );
				break;
		}

		this.logger.updateConfig( this.loggerConfigOptions );
	}

	restoreLoggerConfig() {
		this.setDefaultConfig();
		this.restoreConfig = false;
	}

	getLoggerConfig(): INGXLoggerConfig {
		return { ...this.loggerConfigOptions };
	}

	setLogLevel( level: NgxLoggerLevel, restoreWithLogOutput?: boolean ) {
		this.loggerConfigOptions.level = level;
		this.restoreConfig = this.restoreConfig ? true : restoreWithLogOutput;
		this.logger.updateConfig( this.loggerConfigOptions );
	}

	setDisableConsoleLogging( disableConsoleLog: boolean, restoreWithLogOutput?: boolean ) {
		this.loggerConfigOptions.disableConsoleLogging = disableConsoleLog;
		this.restoreConfig = this.restoreConfig ? true : restoreWithLogOutput;
		this.logger.updateConfig( this.loggerConfigOptions );
	}

	setServerLogLevel( serverLogLevel: NgxLoggerLevel, restoreWithLogOutput?: boolean ) {
		this.loggerConfigOptions.serverLogLevel = serverLogLevel;
		this.restoreConfig = this.restoreConfig ? true : restoreWithLogOutput;
		this.logger.updateConfig( this.loggerConfigOptions );
	}

	setServerLoggingUrl( serverLoggingUrl: string, restoreWithLogOutput?: boolean ) {
		this.loggerConfigOptions.serverLoggingUrl = serverLoggingUrl;
		this.restoreConfig = this.restoreConfig ? true : restoreWithLogOutput;
		this.logger.updateConfig( this.loggerConfigOptions );
	}

	setHttpResponseType( httpResponseType: 'arraybuffer' | 'blob' | 'text' | 'json', restoreWithLogOutput?: boolean ) {
		this.loggerConfigOptions.httpResponseType = httpResponseType;
		this.restoreConfig = this.restoreConfig ? true : restoreWithLogOutput;
		this.logger.updateConfig( this.loggerConfigOptions );
	}

	setEnableSourceMaps( enableSourceMaps: boolean, restoreWithLogOutput?: boolean ) {
		this.loggerConfigOptions.enableSourceMaps = enableSourceMaps;
		this.restoreConfig = this.restoreConfig ? true : restoreWithLogOutput;
		this.logger.updateConfig( this.loggerConfigOptions );
	}

	private pushLog( logType: NgxLoggerLevel, message: any[] ) {
		switch ( logType ) {
			case NgxLoggerLevel.TRACE:
				this.logger.trace( message );
				break;
			case NgxLoggerLevel.DEBUG:
				this.logger.debug( message );
				break;
			case NgxLoggerLevel.INFO:
				this.logger.info( message );
				break;
			case NgxLoggerLevel.LOG:
				this.logger.log( message );
				break;
			case NgxLoggerLevel.WARN:
				this.logger.warn( message );
				break;
			case NgxLoggerLevel.ERROR:
				this.logger.error( message );
				break;
			case NgxLoggerLevel.FATAL:
				this.logger.fatal( message );
				break;
		}
		if ( this.restoreConfig ) { this.restoreLoggerConfig(); }
	}

	trace( ...message: any[] ) {
		this.pushLog( NgxLoggerLevel.TRACE, message );
	}

	debug( ...message: any[] ) {
		this.pushLog( NgxLoggerLevel.DEBUG, message );
	}

	info( ...message: any[] ) {
		this.pushLog( NgxLoggerLevel.INFO, message );
	}

	log( ...message: any[] ) {
		this.pushLog( NgxLoggerLevel.LOG, message );
	}

	warn( ...message: any[] ) {
		this.pushLog( NgxLoggerLevel.WARN, message );
	}

	error( ...message: any[] ) {
		this.pushLog( NgxLoggerLevel.ERROR, message );
	}

	fatal( ...message: any[] ) {
		this.pushLog( NgxLoggerLevel.FATAL, message );
	}
}
